// Firebase Realtime Database Chat Service
// Updated for new architecture: conversations/{conversation_id}/metadata|participants|messages|typing

import { 
  getDatabase, 
  ref, 
  onValue, 
  off, 
  push, 
  set, 
  update,
  query,
  orderByChild,
  limitToLast,
  DatabaseReference,
  DataSnapshot,
  remove
} from 'firebase/database';
import { auth } from '../config/firebase';
import { fetchCurrentUserProfile } from './api';
import {
  FirebaseMessage,
  FirebaseConversation,
  ChatMessage,
  ChatParticipant,
  FirebaseConfig
} from '../types/Chat';

// Interface for Firebase message data
interface FirebaseMessageData {
  messageId?: string;
  senderId?: string;
  sender_id?: string;
  senderName?: string;
  sender_name?: string;
  senderProfilePicture?: string;
  text?: string;
  text_content?: string;
  type?: 'text' | 'system';
  message_type?: 'text' | 'system';
  timestamp: string;
  createdAt?: number;
  created_at?: number;
  isEdited?: boolean;
  is_edited?: boolean;
  isDeleted?: boolean;
  is_deleted?: boolean;
  editedAt?: string | null;
  edited_at?: string | null;
  readBy?: Record<string, string>;
  read_by?: Record<string, string>;
  imageUrl?: string | null;
  image_url?: string | null;
}

const database = getDatabase();

// Track active listeners for cleanup
const activeListeners = new Map<string, DatabaseReference>();

// Initialize Firebase with configuration from backend if needed
export const initializeFirebaseChat = async (config?: FirebaseConfig): Promise<void> => {
  console.log('🔥 Firebase Chat initialized with new architecture');
  
  if (config) {
    console.log('🔧 Using custom Firebase config:', {
      project_id: config.project_id,
      database_url: config.database_url
    });
  }
};

/**
 * Subscribe to messages in a conversation using new structure
 * conversations/{conversation_id}/messages/{message_id}
 */
export const subscribeToMessages = (
  conversationId: string,
  onMessagesUpdate: (messages: ChatMessage[]) => void,
  limit: number = 50
): (() => void) => {
  console.log('📡 Subscribing to messages for conversation:', conversationId);

  const messagesRef = ref(database, `conversations/${conversationId}/messages`);
  const messagesQuery = query(messagesRef, orderByChild('createdAt'), limitToLast(limit));

  // Track this listener for cleanup
  activeListeners.set(`messages_${conversationId}`, messagesRef);

  const unsubscribe = onValue(messagesQuery, (snapshot: DataSnapshot) => {
    const messagesData = snapshot.val() || {};
    const messages: ChatMessage[] = [];

    Object.entries(messagesData).forEach(([messageId, messageDataRaw]) => {
      // Type assertion for Firebase message data
      const messageData = messageDataRaw as FirebaseMessageData;
      
      // Convert Firebase message format to ChatMessage format
      const message: ChatMessage = {
        messageId: messageData.messageId || messageId,
        senderId: messageData.senderId || messageData.sender_id || '',
        senderName: messageData.senderName || messageData.sender_name || '',
        senderProfilePicture: messageData.senderProfilePicture || undefined,
        text: messageData.text || messageData.text_content || '',
        type: messageData.type || messageData.message_type || 'text',
        timestamp: messageData.timestamp,
        createdAt: messageData.createdAt || messageData.created_at || Date.now(),
        isEdited: messageData.isEdited || messageData.is_edited || false,
        isDeleted: messageData.isDeleted || messageData.is_deleted || false,
        editedAt: messageData.editedAt || messageData.edited_at || null,
        readBy: messageData.readBy || messageData.read_by || {},
        imageUrl: messageData.imageUrl || messageData.image_url || null
      };
      messages.push(message);
    });

    // Sort messages by creation time
    messages.sort((a, b) => a.createdAt - b.createdAt);

    console.log(`📨 Received ${messages.length} messages for conversation:`, conversationId);
    onMessagesUpdate(messages);
  }, (error) => {
    console.error('❌ Error subscribing to messages:', error);
    onMessagesUpdate([]);
  });

  return () => {
    console.log('🔇 Unsubscribing from messages:', conversationId);
    unsubscribe();
    activeListeners.delete(`messages_${conversationId}`);
  };
};

/**
 * Subscribe to typing indicators using new structure
 * conversations/{conversation_id}/typing/{user_id}
 */
export const subscribeToTypingIndicators = (
  conversationId: string,
  onTypingUpdate: (typingUsers: Record<string, boolean>) => void
): (() => void) => {
  console.log('👀 Subscribing to typing indicators for conversation:', conversationId);

  const typingRef = ref(database, `conversations/${conversationId}/typing`);
  activeListeners.set(`typing_${conversationId}`, typingRef);

  const unsubscribe = onValue(typingRef, (snapshot: DataSnapshot) => {
    const typingData = snapshot.val() || {};
    const typingUsers: Record<string, boolean> = {};
    const now = Date.now();

    // Filter out expired typing indicators (older than 5 seconds)
    Object.entries(typingData).forEach(([userId, timestampRaw]) => {
      const timestamp = timestampRaw as number;
      if (typeof timestamp === 'number' && (now - timestamp) < 5000) {
        typingUsers[userId] = true;
      }
    });

    console.log('⌨️ Typing users updated:', Object.keys(typingUsers));
    onTypingUpdate(typingUsers);
  }, (error) => {
    console.error('❌ Error subscribing to typing indicators:', error);
    onTypingUpdate({});
  });

  return () => {
    console.log('🔇 Unsubscribing from typing indicators:', conversationId);
    unsubscribe();
    activeListeners.delete(`typing_${conversationId}`);
  };
};

/**
 * Subscribe to conversation metadata using new structure
 * conversations/{conversation_id}/metadata & participants
 */
export const subscribeToConversation = (
  conversationId: string,
  onConversationUpdate: (conversation: Partial<FirebaseConversation> | null) => void
): (() => void) => {
  console.log('📋 Subscribing to conversation metadata:', conversationId);

  const conversationRef = ref(database, `conversations/${conversationId}`);
  activeListeners.set(`conversation_${conversationId}`, conversationRef);

  const unsubscribe = onValue(conversationRef, (snapshot: DataSnapshot) => {
    const conversationData = snapshot.val();
    
    if (!conversationData) {
      console.log('❌ Conversation not found:', conversationId);
      onConversationUpdate(null);
      return;
    }

    const conversation: Partial<FirebaseConversation> = {
      metadata: conversationData.metadata || {},
      participants: conversationData.participants || {},
      typing: conversationData.typing || {}
    };

    console.log('📋 Conversation metadata updated:', conversationId);
    onConversationUpdate(conversation);
  }, (error) => {
    console.error('❌ Error subscribing to conversation:', error);
    onConversationUpdate(null);
  });

  return () => {
    console.log('🔇 Unsubscribing from conversation:', conversationId);
    unsubscribe();
    activeListeners.delete(`conversation_${conversationId}`);
  };
};

/**
 * Send a real-time message using new structure
 */
export const sendRealtimeMessage = async (
  conversationId: string,
  messageText: string
): Promise<string> => {
  if (!auth.currentUser) {
    throw new Error('User not authenticated');
  }

  if (!messageText.trim()) {
    throw new Error('Message text cannot be empty');
  }

  console.log('📤 Sending real-time message to conversation:', conversationId);

  try {
    // Get user profile to access the uploaded profile picture
    let userProfilePicture: string | undefined;
    let senderDisplayName = auth.currentUser.displayName || auth.currentUser.email || 'Unknown User';
    
    try {
      const userProfile = await fetchCurrentUserProfile();
      userProfilePicture = userProfile.profilePictureUrl;
      // Use the full name from profile if available
      if (userProfile.name) {
        senderDisplayName = userProfile.name;
      }
      console.log('📸 Using backend profile picture:', !!userProfilePicture);
    } catch (profileError) {
      console.warn('⚠️ Could not fetch user profile for chat, using Firebase Auth data:', profileError);
      // Fallback to Firebase Auth photoURL only if backend fails
      userProfilePicture = auth.currentUser.photoURL || undefined;
    }

    const messagesRef = ref(database, `conversations/${conversationId}/messages`);
    const newMessageRef = push(messagesRef);
    
    if (!newMessageRef.key) {
      throw new Error('Failed to generate message key');
    }

    const messageId = `msg_${Date.now()}_${newMessageRef.key}`;
    const now = new Date().toISOString();
    const timestamp = Date.now();

    const messageData: FirebaseMessage = {
      messageId,
      senderId: auth.currentUser.uid,
      senderName: senderDisplayName,
      text: messageText.trim(),
      type: 'text',
      timestamp: now,
      createdAt: timestamp,
      isEdited: false,
      isDeleted: false,
      editedAt: null,
      readBy: {
        [auth.currentUser.uid]: now
      },
      imageUrl: null
    };

    // Only include senderProfilePicture if it exists
    if (userProfilePicture) {
      messageData.senderProfilePicture = userProfilePicture;
    }

    await set(newMessageRef, messageData);
    
    // Update conversation metadata
    const metadataRef = ref(database, `conversations/${conversationId}/metadata`);
    await update(metadataRef, {
      last_message_at: now
    });

    console.log('✅ Real-time message sent successfully:', messageId);
    return messageId;

  } catch (error) {
    console.error('❌ Error sending real-time message:', error);
    throw new Error(`Failed to send message: ${error}`);
  }
};

/**
 * Set typing status using new structure
 */
export const setTypingStatus = async (
  conversationId: string,
  isTyping: boolean
): Promise<void> => {
  if (!auth.currentUser) return;

  console.log(`⌨️ Setting typing status: ${isTyping} for conversation:`, conversationId);

  try {
    const typingRef = ref(database, `conversations/${conversationId}/typing/${auth.currentUser.uid}`);

    if (isTyping) {
      await set(typingRef, Date.now());
    } else {
      await remove(typingRef);
    }

  } catch (error) {
    console.error('❌ Error setting typing status:', error);
  }
};

/**
 * Mark messages as read using new structure
 */
export const markMessageAsRead = async (
  conversationId: string,
  messageId: string
): Promise<void> => {
  if (!auth.currentUser) return;

  try {
    const readByRef = ref(database, `conversations/${conversationId}/messages/${messageId}/readBy/${auth.currentUser.uid}`);
    await set(readByRef, new Date().toISOString());

    console.log('✅ Message marked as read:', messageId);
  } catch (error) {
    console.error('❌ Error marking message as read:', error);
  }
};

/**
 * Mark all messages as read
 */
export const markAllMessagesAsRead = async (
  conversationId: string,
  messages: ChatMessage[]
): Promise<void> => {
  if (!auth.currentUser || messages.length === 0) return;

  console.log(`📖 Marking ${messages.length} messages as read for conversation:`, conversationId);

  try {
    const updates: Record<string, string> = {};
    const timestamp = new Date().toISOString();

    messages.forEach(message => {
      if (message.senderId !== auth.currentUser!.uid) {
        updates[`conversations/${conversationId}/messages/${message.messageId}/readBy/${auth.currentUser!.uid}`] = timestamp;
      }
    });

    if (Object.keys(updates).length > 0) {
      await update(ref(database), updates);
      
      // Update participant's last read timestamp
      const participantRef = ref(database, `conversations/${conversationId}/participants/${auth.currentUser.uid}`);
      await update(participantRef, {
        last_read_at: timestamp
      });
    }

    console.log('✅ All messages marked as read');
  } catch (error) {
    console.error('❌ Error marking messages as read:', error);
  }
};

/**
 * Initialize Firebase conversation structure after creation
 */
export const initializeFirebaseConversation = async (
  conversationId: string,
  activityId: number,
  participants: Record<string, ChatParticipant>
): Promise<void> => {
  console.log('🔧 Initializing Firebase conversation structure:', conversationId);

  try {
    const conversationRef = ref(database, `conversations/${conversationId}`);
    const now = new Date().toISOString();

    const conversationData: FirebaseConversation = {
      metadata: {
        created_at: now,
        activity_id: activityId,
        last_message_at: now,
        participant_count: Object.keys(participants).length
      },
      participants: Object.fromEntries(
        Object.entries(participants).map(([userId, participant]) => [
          userId, 
          {
            ...participant,
            last_read_at: now,
            is_online: false,
            last_seen_at: now
          }
        ])
      ),
      messages: {},
      typing: {}
    };

    await set(conversationRef, conversationData);
    console.log('✅ Firebase conversation structure initialized');

  } catch (error) {
    console.error('❌ Error initializing Firebase conversation:', error);
    throw error;
  }
};

/**
 * Update online presence
 */
export const setUserOnlineStatus = async (isOnline: boolean): Promise<void> => {
  if (!auth.currentUser) return;

  try {
    // This could be enhanced to update presence across all user's conversations
    console.log(`🌐 Setting user online status: ${isOnline}`);
    
    // For now, we'll set this when the user enters specific conversations
    // A more comprehensive approach would maintain a global presence system

  } catch (error) {
    console.error('❌ Error setting online status:', error);
  }
};

/**
 * Update participant online status in a specific conversation
 */
export const updateParticipantPresence = async (
  conversationId: string,
  isOnline: boolean
): Promise<void> => {
  if (!auth.currentUser) return;

  try {
    const participantRef = ref(database, `conversations/${conversationId}/participants/${auth.currentUser.uid}`);
    await update(participantRef, {
      is_online: isOnline,
      last_seen_at: new Date().toISOString()
    });

    console.log(`👤 Updated presence for conversation ${conversationId}: ${isOnline}`);
  } catch (error) {
    console.error('❌ Error updating participant presence:', error);
  }
};

/**
 * Get unread message count for a conversation
 */
export const getUnreadMessageCount = (
  messages: ChatMessage[],
  userId: string
): number => {
  return messages.filter(message => 
    message.senderId !== userId && 
    (!message.readBy || !message.readBy[userId])
  ).length;
};

/**
 * Clean up all active listeners
 */
export const cleanupAllListeners = (): void => {
  console.log('🧹 Cleaning up all Firebase listeners');
  
  activeListeners.forEach((ref, key) => {
    console.log(`🔇 Cleaning up listener: ${key}`);
    off(ref);
  });
  
  activeListeners.clear();
};

/**
 * Generate unique message ID
 */
export const generateMessageId = (): string => {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Format Firebase timestamp
 */
export const formatFirebaseTimestamp = (timestamp: number | string): string => {
  const date = typeof timestamp === 'number' ? new Date(timestamp) : new Date(timestamp);
  return date.toISOString();
};

/**
 * Validate conversation ID format
 */
export const isValidConversationId = (conversationId: string): boolean => {
  return !!(conversationId && conversationId.includes('activity_') && conversationId.includes('_chat_'));
};

console.log('🔥 Firebase Chat Service loaded with new architecture');